import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Button,
  useTheme,
  Grid
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/AutoStoriesOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { Link } from 'react-router-dom';

const Home = () => {
  const theme = useTheme();

  // External SVG URLs
  const placeholder = 'https://qallix-public.s3.ap-southeast-1.amazonaws.com/QallixAcademy/SoftwareCourses/1/thumbnail.png';
  const purple = 'https://qallix-public.s3.ap-southeast-1.amazonaws.com/QallixAcademy/theme/assets/top-left.svg';
  const red1 = 'https://qallix-public.s3.ap-southeast-1.amazonaws.com/QallixAcademy/theme/assets/top-right.svg';
  const red2 = 'https://qallix-public.s3.ap-southeast-1.amazonaws.com/QallixAcademy/theme/assets/bottom-left.svg';
  const green = 'https://qallix-public.s3.ap-southeast-1.amazonaws.com/QallixAcademy/theme/assets/bottom-right.svg';
  const ctaBanner = 'https://qallix-public.s3.ap-southeast-1.amazonaws.com/QallixAcademy/theme/assets/CTA.png';

  // Only the beginner course
  const beginnerCourse = {
    title: 'Getting Started With Qallix',
    lessons: '2 lessons',
    image: placeholder,
    level: 'Basic',
    duration: '0h 15m',
    slug: 'getting-started-with-qallix',
    description: 'Start your journey with Qallix through this beginner course.'
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'Basic':
        return '#26aa99';
      case 'Intermediate':
        return '#332872';
      case 'Advanced':
        return '#e33e1d';
      default:
        return '#9E9E9E';
    }
  };

  return (
    <Box sx={{ width: '100%' }}>

      {/* Hero Section */}
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        {/* Decorative elements - Only visible on desktop */}
        <Box
          component="img"
          src={purple}
          alt="Decoration"
          sx={{
            position: 'absolute',
            top: '20%',
            left: '20%',
            width: '100px',
            height: '100px',
            zIndex: 2,
            display: { xs: 'none', md: 'block' }, // Hidden on mobile, visible on desktop
            [theme.breakpoints.down('md')]: {
              width: '60px',
              height: '60px'
            }
          }}
        />
        <Box
          component="img"
          src={red1}
          alt="Decoration"
          sx={{
            position: 'absolute',
            top: '20%',
            right: '20%',
            width: '100px',
            height: '100px',
            zIndex: 2,
            display: { xs: 'none', md: 'block' }, // Hidden on mobile, visible on desktop
            [theme.breakpoints.down('md')]: {
              width: '60px',
              height: '60px'
            }
          }}
        />
        <Box
          component="img"
          src={green}
          alt="Decoration"
          sx={{
            position: 'absolute',
            top: '70%',
            right: '20%',
            width: '100px',
            height: '100px',
            zIndex: 2,
            display: { xs: 'none', md: 'block' }, // Hidden on mobile, visible on desktop
            [theme.breakpoints.down('md')]: {
              width: '60px',
              height: '60px'
            }
          }}
        />
        <Box
          component="img"
          src={red2}
          alt="Decoration"
          sx={{
            position: 'absolute',
            top: '70%',
            left: '20%',
            width: '100px',
            height: '100px',
            zIndex: 2,
            display: { xs: 'none', md: 'block' }, // Hidden on mobile, visible on desktop
            [theme.breakpoints.down('md')]: {
              width: '60px',
              height: '60px'
            }
          }}
        />

        {/* Full-Width Hero Content */}
        <Box sx={{
          display: 'flex',
          backgroundColor: '#ffffff',
          textAlign: { xs: 'left', sm: 'center' },
          position: 'relative',
          zIndex: 1,
          width: '100%',
          py: { xs: 2, sm: 2, md: 8},
          px: { sm: 4, md: 6 } // Responsive padding
        }}>
          <Container maxWidth="lg" sx={{
            display: 'flex',
            justifyContent: 'center'
          }}>
            <Box sx={{
              mt: { xs: '30px', sm: '50px' },
              maxWidth: 800,
              width: '100%',
              mx: 'auto'
            }}>
              <Typography variant="h2" sx={{
                fontFamily: "'Satoshi', sans-serif",
                fontWeight: 'bolder',
                mb: 4,
                color: '#000000',
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem', lg: '5.2rem' },
                lineHeight: 1.2,
                textAlign: { xs: 'left', sm: 'center' }
              }}>
                Learn and Grow with Top {' '}
                <Box component="span" sx={{ color: '#7F59EB' }}>
                  Online Courses
                </Box>
              </Typography>
              <Typography variant="h5" sx={{
                fontFamily: "'Satoshi', sans-serif",
                mb: 6,
                color: '#000000',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                lineHeight: 1.6
              }}>
                Discover top online courses to upgrade your skills and stay ahead.
                Learn from experts and enhance your expertise at your own pace.
              </Typography>
              <Box sx={{
                display: 'flex',
                justifyContent: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 2, sm: 4 },
                flexWrap: { xs: 'wrap', sm: 'nowrap' }
              }}>
                <Button
                  component={Link}
                  to="/courses"
                  variant="contained"
                  sx={{
                    backgroundColor: '#332872',
                    color: '#ffffff',
                    fontWeight: 'bold',
                    fontFamily: "'Satoshi', sans-serif",
                    borderRadius: '25px',
                    fontSize: { xs: '0.9rem', sm: '1.1rem' },
                    textTransform: 'none',
                    py: { xs: '8px', sm: '10px' },
                    '&:hover': { backgroundColor: '#332872' }
                  }}
                >
                  Explore Courses
                </Button>
                <Button
                  variant="outlined"
                  sx={{
                    borderColor: '#000000ff',
                    color: '#000000ff',
                    fontWeight: 'bold',
                    fontFamily: "'Satoshi', sans-serif",
                    borderRadius: '25px',
                    fontSize: { xs: '0.9rem', sm: '1.1rem' },
                    textTransform: 'none',
                    py: { xs: '8px', sm: '10px' },
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#2a2930ff'
                    }
                  }}
                >
                  Contact Us
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>

      {/* Beginner Course Section */}
      <Box sx={{
        backgroundColor: '#F8F8F8',
        width: '100%',
        py: 8
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center" justifyContent='space-between'>
            <Grid item xs={12} md={6} maxWidth='600px'>
              <Typography sx={{
                fontFamily: "'Satoshi', sans-serif",
                fontWeight: 'bold',
                mb: 3,
                color: '#000000',
                fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
                lineHeight: 1.2
              }}>
                Start Your Learning Journey
              </Typography>
              <Typography sx={{
                fontFamily: "'Satoshi', sans-serif",
                mb: 4,
                color: '#000000',
                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                lineHeight: 1.6
              }}>
                Begin with our foundational course designed for newcomers.
                This introductory program will give you the essential knowledge
                and skills to get started with confidence.
              </Typography>
              <Button
                component={Link}
                to="/courses"
                variant="contained"
                sx={{
                  backgroundColor: '#332872',
                    color: '#ffffff',
                    fontWeight: 'bold',
                    fontFamily: "'Satoshi', sans-serif",
                    borderRadius: '25px',
                    fontSize: { xs: '0.9rem', sm: '1.1rem' },
                    textTransform: 'none',
                    py: { xs: '8px', sm: '10px' },
                    '&:hover': { backgroundColor: '#332872' }
                }}
              >
                View All Courses
              </Button>
            </Grid>

            <Grid item xs={12} md={6} maxWidth='500px'>
              <Link to={`/courses/${beginnerCourse.slug}`} style={{ textDecoration: 'none' }}>
                <Card sx={{
                  borderRadius: 3,
                  boxShadow: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                    cursor: 'pointer'
                  }
                }}>
                  <Box sx={{ px: 2, pt: 2 }}>
                    <CardMedia
                      component="img"
                      image={beginnerCourse.image}
                      alt={beginnerCourse.title}
                      sx={{
                        borderRadius: 2,
                        width: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    <CardContent sx={{ p: 0, flexGrow: 1, pt: 2, pb: 2 }}>
                      <Typography
                        sx={{
                          fontSize: { xs: '18px', sm: '20px' },
                          fontWeight: '700',
                          fontFamily: "'Satoshi', sans-serif",
                          mb: 1
                        }}
                      >
                        {beginnerCourse.title}
                      </Typography>

                      <Typography
                        sx={{
                          color: '#666',
                          fontFamily: "'Satoshi', sans-serif",
                          mb: 2,
                          fontSize: { xs: '0.9rem', sm: '0.95rem' }
                        }}
                      >
                        {beginnerCourse.description}
                      </Typography>

                      <Box sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        flexWrap: 'wrap'
                      }}>
                        <Chip
                          label={beginnerCourse.level}
                          size="small"
                          sx={{
                            backgroundColor: getLevelColor(beginnerCourse.level),
                            color: '#ffffff',
                            fontWeight: 'bold',
                            borderRadius: '15px',
                            px: 1,
                            py: 1.5,
                            fontFamily: "'Satoshi', sans-serif",
                            fontSize: { xs: '0.7rem', sm: '0.8rem' }
                          }}
                        />
                        <Chip
                          icon={<MenuBookIcon />}
                          label={beginnerCourse.lessons}
                          size="small"
                          sx={{
                            backgroundColor: '#FFFFFF',
                            color: '#000000',
                            fontWeight: 'bold',
                            borderRadius: '15px',
                            border: '1px solid #D1D5DB',
                            px: 1,
                            py: 1.5,
                            fontFamily: "'Satoshi', sans-serif",
                            fontSize: { xs: '0.7rem', sm: '0.8rem' }
                          }}
                        />
                        <Chip
                          icon={<TimerOutlinedIcon />}
                          label={beginnerCourse.duration}
                          size="small"
                          sx={{
                            backgroundColor: '#FFFFFF',
                            color: '#000000',
                            fontWeight: 'bold',
                            borderRadius: '15px',
                            border: '1px solid #D1D5DB',
                            px: 1,
                            py: 1.5,
                            fontFamily: "'Satoshi', sans-serif",
                            fontSize: { xs: '0.7rem', sm: '0.8rem' }
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Box>
                </Card>
              </Link>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Banner Section */}
      <Box sx={{
        width: '100%',
        py: 6,
        backgroundColor: '#F8F8F8',
        display: { xs: 'none', sm: 'block' } // Hide on mobile, show on small and up
      }}>
        <Container maxWidth="lg">
          <a
            href="https://signup.qallix.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none' }}
          >
            <Box
              component="img"
              src={ctaBanner}
              alt="Call to Action"
              sx={{
                width: '100%',
                borderRadius: 2,
                cursor: 'pointer',
                boxShadow: 1,
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.01)',
                  boxShadow: 3
                },
                [theme.breakpoints.down('sm')]: {
                  borderRadius: 0
                }
              }}
            />
          </a>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;